// Country-aware ZIP/postal validation utilities.
// Keep patterns lightweight; provide clear hints where possible.

export const CountryCodes = [
  'US',
  'CA',
  'GB',
  'UK',
  'AU',
  'NZ',
  'DE',
  'FR',
  'IT',
  'ES',
  'NL',
  'BE',
  'CH',
  'AT',
  'DK',
  'SE',
  'NO',
  'FI',
  'IE',
  'PT',
  'BR',
  'MX',
  'JP',
  'CN',
  'IN',
  'VN',
];

export type CountryCode = (typeof CountryCodes)[number];

const alias = (code: CountryCode): CountryCode => (code === 'UK' ? 'GB' : code);

// Minimal, commonly accepted patterns (not exhaustive for all local quirks)
const ZIP_PATTERNS: Record<CountryCode, RegExp> = {
  US: /^\d{5}(?:-\d{4})?$/, // 12345 or 12345-6789
  CA: /^[A-Z]\d[A-Z][ ]?\d[A-Z]\d$/, // A1A 1A1
  GB: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/, // Approx UK postcode
  UK: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/, // Alias to GB (not used directly)
  AU: /^\d{4}$/,
  NZ: /^\d{4}$/,
  DE: /^\d{5}$/,
  FR: /^\d{5}$/,
  IT: /^\d{5}$/,
  ES: /^\d{5}$/,
  NL: /^\d{4}\s?[A-Z]{2}$/, // 1234 AB
  BE: /^\d{4}$/,
  CH: /^\d{4}$/,
  AT: /^\d{4}$/,
  DK: /^\d{4}$/,
  SE: /^\d{3}\s?\d{2}$/, // 123 45 or 12345
  NO: /^\d{4}$/,
  FI: /^\d{5}$/,
  IE: /^[A-Z]\d{2}\s?[A-Z0-9]{4}$/, // Eircode (approx)
  PT: /^\d{4}-\d{3}$/, // 1234-567
  BR: /^\d{5}-?\d{3}$/, // 12345-678 or 12345678
  MX: /^\d{5}$/,
  JP: /^\d{3}-?\d{4}$/, // 123-4567 or 1234567
  CN: /^\d{6}$/,
  IN: /^\d{6}$/,
  VN: /^\d{6}$/,
};

const ERROR_HINTS: Partial<Record<CountryCode, string>> = {
  US: 'Format: 12345 or 12345-6789',
  CA: 'Format: A1A 1A1',
  GB: 'Format: SW1A 1AA',
  AU: '4 digits (e.g., 2000)',
  NZ: '4 digits (e.g., 6011)',
  DE: '5 digits (e.g., 10115)',
  FR: '5 digits (e.g., 75001)',
  NL: 'Format: 1234 AB',
  PT: 'Format: 1234-567',
  BR: 'Format: 12345-678',
  JP: 'Format: 123-4567',
  IE: 'Format: A65 F4E2',
};

// Uppercase normalization for alphanumeric postal formats
const UPPER_CASE_COUNTRIES = new Set<CountryCode>([
  'CA',
  'GB',
  'UK',
  'NL',
  'IE',
]);

export const normalizeZip = (countryCode: CountryCode, raw: string): string => {
  if (!raw) return '';
  const cc = alias(countryCode);
  let s = raw.trim();
  if (UPPER_CASE_COUNTRIES.has(cc)) s = s.toUpperCase();
  // Collapse multiple spaces to single (keeps user formatting readable)
  s = s.replace(/\s+/g, ' ');
  return s;
};

export const validateZipCode = (
  rawValue: string,
  countryCode: CountryCode = 'US'
): { valid: boolean; normalized: string; hint?: string } => {
  const cc = alias(countryCode);
  const normalized = normalizeZip(cc, rawValue);

  // Empty value is handled by the component when required=true
  if (!normalized) {
    return { valid: false, normalized, hint: 'Postal code is required' };
  }

  const pattern = ZIP_PATTERNS[cc] || /^[A-Za-z0-9][A-Za-z0-9\- ]{2,10}$/;
  const valid = pattern.test(normalized);
  const hint = valid
    ? undefined
    : ERROR_HINTS[cc] || 'Invalid postal code format';

  return { valid, normalized, hint };
};

// Format ZIP/postal code while typing for better readability
// - Inserts common separators (space/hyphen) for specific countries
// - Restricts characters to valid sets per country as the user types
export const formatZipOnType = (
  countryCode: CountryCode,
  raw: string
): string => {
  const cc = alias(countryCode); // Normalize 'UK' -> 'GB'
  if (!raw) return '';

  switch (cc) {
    case 'US': {
      // 12345 or 12345-6789
      const digits = raw.replace(/\D/g, '').slice(0, 9);
      if (digits.length <= 5) return digits;
      return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    }
    case 'CA': {
      // A1A 1A1
      const alnum = raw
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 6);
      if (alnum.length <= 3) return alnum;
      return `${alnum.slice(0, 3)} ${alnum.slice(3)}`;
    }
    case 'GB': {
      // SW1A 1AA (approximation; inserts space before last 3)
      const alnum = raw
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 8);
      if (alnum.length <= 3) return alnum;
      return `${alnum.slice(0, alnum.length - 3)} ${alnum.slice(alnum.length - 3)}`;
    }
    case 'NL': {
      // 1234 AB
      const trimmed = raw
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 6);
      if (trimmed.length <= 4) return trimmed;
      return `${trimmed.slice(0, 4)} ${trimmed.slice(4)}`;
    }
    case 'PT': {
      // 1234-567
      const digits = raw.replace(/\D/g, '').slice(0, 7);
      if (digits.length <= 4) return digits;
      return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    }
    case 'BR': {
      // 12345-678
      const digits = raw.replace(/\D/g, '').slice(0, 8);
      if (digits.length <= 5) return digits;
      return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    }
    case 'JP': {
      // 123-4567
      const digits = raw.replace(/\D/g, '').slice(0, 7);
      if (digits.length <= 3) return digits;
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    case 'SE': {
      // 123 45 (or 12345)
      const digits = raw.replace(/\D/g, '').slice(0, 5);
      if (digits.length <= 3) return digits;
      return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    }
    // Numeric-only countries; keep to a reasonable max length while typing
    case 'AU':
    case 'NZ':
    case 'DE':
    case 'FR':
    case 'IT':
    case 'ES':
    case 'BE':
    case 'CH':
    case 'AT':
    case 'DK':
    case 'NO':
    case 'FI':
    case 'MX':
    case 'CN':
    case 'IN':
    case 'VN': {
      return raw.replace(/\D/g, '').slice(0, 6);
    }
    default: {
      // Fallback: uppercase alphanumeric; separators handled by normalize/validate
      return raw.toUpperCase().replace(/[^A-Z0-9 ]/g, '');
    }
  }
};
